import type { SchemaObject } from 'json-schema-traverse';
import { cloneDeep, unset } from 'lodash-es';

import { flattenResolvedSchemaTree, resolveSchemaTreeFromForm } from './resolveSchemaTreeFromForm';
import allowlistOfRemovablePaths from './schema-conditional-paths.json';

interface ShakeOffOptions {
  /**
   * A callback function that is called when a path is removed from the data.
   * @param path - The path that was removed from the data.
   */
  onPathRemoved?: (path: string) => void;

  /**
   * Controls whether to remove all properties or only those in the allowlist.
   */
  ignoreAllowlist?: boolean;
}

export function shakeOffUnusedProperties<T extends Record<string, any>>(
  schema: SchemaObject,
  data: T,
  options: ShakeOffOptions = {},
): T {
  // We "re-flatten" the schema within this function as we need it to be resolved
  // with `includeOthers` option. Which is not available in "pre-flatten" schema
  // from web application schema store
  const flatSchema = flattenResolvedSchemaTree(resolveSchemaTreeFromForm(schema, data, false, true, true));

  const allPaths = getPaths(data);
  const prunedData = cloneDeep(data);

  for (const rawPath of allPaths) {
    if (rawPath.startsWith('risk_details.split_by_sections')) {
      // `split_by_sections` is a special field that is not part of the schema but exists in the data
      // we need to keep it
      continue;
    }

    // If there are paths that are ending with number, it could mean that these are arrays of scalars,
    // such values are mapped in schema as an entity of array type.
    // Hence we need to remove the number from the path, so it aligns with how they are defined in schema.
    const path = rawPath.replace(/\.\d+$/, '');

    // When checking if a path exists in the schema, we check if path is a prefix of item in schema because:
    // 1. A path in the data can be either a parent node (e.g., "user.address") or a leaf node (e.g., "user.address.street")
    // 2. The schema typically defines validation rules for leaf nodes (final properties)
    // 3. We don't want to remove a parent node just because the schema doesn't have an exact match for it
    //    For example, if we have data path "user.address" and schema has "user.address.street",
    //    we should keep "user.address" since it's a valid parent of a schema-defined path
    const escapedPath = escapeRegExp(path);
    if (flatSchema.find((item) => new RegExp(`^${escapedPath}\\b`).test(item.id))) {
      // Current path is covered by the schema, so keep it
      continue;
    }

    if (!options.ignoreAllowlist && !allowlistOfRemovablePaths.includes(path.replace(/\.\d+\b/g, '.0'))) {
      // Path is not part of allowed properties
      continue;
    }

    // We failed to find the schema node that covers the current path, so remove it
    unset(prunedData, path);

    options.onPathRemoved?.(path);
  }

  return prunedData;
}

function getPaths<T extends Record<string, any>>(obj: T, currentPath: string = ''): string[] {
  const paths: string[] = [];

  if (typeof obj !== 'object' || obj === null) {
    return paths;
  }

  for (const key of Object.keys(obj)) {
    const newPath = currentPath ? `${currentPath}.${key}` : key;
    paths.push(newPath);

    if (typeof obj[key] === 'object' && obj[key] !== null) {
      paths.push(...getPaths(obj[key], newPath));
    }
  }

  return paths;
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
